import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQueryWithReauth } from "./baseQuery";
import { IInOfficeEvent } from "../types/formAssignments/IInOfficeEvent";
import { IGetResponse } from "../types/apiResponse/IGetResponse.interface";
import { IPaging } from "../types/apiResponse/Paging";
import { queryStringBuilder } from "src/helperFunctions/api/queryStringBuilder";
import { DeleteFormApiRequest } from "../types/formAssignments/DeleteFormApiRequest.interface";
import { IAssignFormsApiResponse } from "../types/apiResponse/IAssignformsApiResponse.interface";
import { IAssignFormsApiRequest } from "../types/apiRequest/IAssignFormsApiRequest.interface";
import { IUpdateFormAssignmentRequest } from "../types/formAssignments/IUpdateFormAssignmentRequest.interface";
import { IPatientFamilyMember } from "../types/patients/IPatientFamilyMember.interface";

const addUniqueKeyToEvent = (patient: IInOfficeEvent): IInOfficeEvent => {
  const { patient: patientData, appointment, formAssignment } = patient;
  const appointmentOrFormAssignmentId = appointment?.id ?? formAssignment?.id;
  return {
    ...patient,
    uniqueKey: appointmentOrFormAssignmentId
      ? `${patientData.id}-${appointmentOrFormAssignmentId}`
      : patientData.id,
  };
};

export const formAssignmentsApi = createApi({
  reducerPath: "formAssignmentsApi",
  baseQuery: baseQueryWithReauth(
    `${import.meta.env.VITE_APP_YAPI_DOCMGR}api/v1`
  ),
  tagTypes: ["InOfficeEvents", "OutStandingTasks", "PendingForms", "Forms"],
  endpoints: (builder) => ({
    getInOfficeEvents: builder.query<
      IGetResponse<IInOfficeEvent[]>,
      Pick<IPaging, "pageIndex" | "pageSize">
    >({
      query: ({ pageIndex, pageSize }) => {
        const queryParams = queryStringBuilder({ pageIndex, pageSize });
        return `/patients/inOffice/?${queryParams}`;
      },
      providesTags: ["InOfficeEvents"],
      transformResponse: (response: IGetResponse<IInOfficeEvent[]>) => ({
        ...response,
        data: response.data.map((event) => addUniqueKeyToEvent(event)),
      }),
    }),
    getInOfficeEventsOfPatient: builder.query<
      IGetResponse<IInOfficeEvent[]>,
      string
    >({
      query: (patientId) => `patients/inOffice/${patientId}`,
      providesTags: (_res, _err, patientId) => [
        { type: "InOfficeEvents", id: patientId },
      ],
      transformResponse: (response: IGetResponse<IInOfficeEvent[]>) => ({
        ...response,
        data: response.data.map((event) => addUniqueKeyToEvent(event)),
      }),
    }),
    getInOfficeFamilyMembers: builder.query<IPatientFamilyMember[], string>({
      query: (patientId) => `patients/${patientId}/familyMembersInOffice`,
      providesTags: (_res, _err, id) => [{ type: "InOfficeEvents", id }],
    }),
    deleteForm: builder.mutation<void, DeleteFormApiRequest>({
      query({ patientId, formAssignmentId, formId, responseId }) {
        return {
          url: `patients/${patientId}/formAssignments/${formAssignmentId}/forms/${formId}?responseId=${responseId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["InOfficeEvents", "PendingForms"],
    }),
    createFormAssignment: builder.mutation<
      IAssignFormsApiResponse,
      IAssignFormsApiRequest
    >({
      query({
        packageName,
        patientId,
        forms,
        pendingPatientCheckIn = false,
        appointmentId = null,
      }) {
        return {
          url: `patients/${patientId}/formAssignments`,
          method: "POST",
          body: {
            forms,
            packageName,
            pendingPatientCheckIn,
            appointmentId,
          },
        };
      },
    }),
    updateFormAssignment: builder.mutation<
      IAssignFormsApiResponse,
      IUpdateFormAssignmentRequest
    >({
      query({
        packageName,
        patientId,
        forms,
        formAssignmentId,
        appointmentId,
      }) {
        return {
          url: `patients/${patientId}/formAssignments/${formAssignmentId}`,
          method: "PATCH",
          body: {
            forms,
            packageName,
            appointmentId,
          },
        };
      },
      invalidatesTags: ["PendingForms"],
    }),
    createDottLinkNotification: builder.mutation<
      {
        patientId: string;
        id: string;
        formAssignmentId: string;
        sentAt: number;
        type: number;
        dottLink: string;
      }[],
      {
        patientId: string;
        formAssignmentId: string;
      }[]
    >({
      query: (body) => ({
        url: `patients/${body[0].patientId}/formAssignments/notifications/dottlinks`,
        method: "POST",
        body,
      }),
    }),
  }),
});
export const {
  useGetInOfficeEventsQuery,
  useDeleteFormMutation,
  useCreateFormAssignmentMutation,
  useUpdateFormAssignmentMutation,
  useCreateDottLinkNotificationMutation,
  useGetInOfficeFamilyMembersQuery,
  useLazyGetInOfficeEventsOfPatientQuery,
} = formAssignmentsApi;
